import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, Search, Siren } from "lucide-react";

import { useListIncidents } from "@/api/generated";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsInternal } from "@/lib/lookups";
import { useNow } from "@/lib/now";
import { cn } from "@/lib/utils";
import { useStreamState } from "@/stream/stream";
import { useUI } from "@/store/ui";
import { Logo, NavList, SoundToggle, UserMenu } from "./Rail";

function StreamPill() {
  const { state } = useStreamState();
  const meta = {
    live: { label: "Live", dot: "bg-status-up", text: "text-muted-foreground" },
    connecting: { label: "Connecting…", dot: "bg-status-unknown animate-pulse", text: "text-muted-foreground" },
    offline: { label: "Reconnecting…", dot: "bg-status-degraded animate-pulse", text: "text-status-degraded" },
    stale: { label: "Stale", dot: "bg-status-down", text: "text-status-down" },
  }[state];
  return (
    <span className={cn("flex items-center gap-1.5 text-xs", meta.text)} title="Live update stream">
      <span className={cn("size-2 rounded-full", meta.dot)} aria-hidden />
      {meta.label}
    </span>
  );
}

function UtcClock() {
  const now = useNow();
  return (
    <span className="tabular hidden text-xs text-muted-foreground sm:inline" title="UTC">
      {new Date(now).toISOString().slice(11, 19)} UTC
    </span>
  );
}

export function TopBar() {
  const [navOpen, setNavOpen] = useState(false);
  const setPalette = useUI((s) => s.setPalette);
  const internal = useIsInternal();
  const incidents = useListIncidents(undefined, { query: { enabled: internal } });
  const active = (incidents.data ?? []).filter((i) => !i.suppressed_by);
  const unacked = active.filter((i) => i.status === "OPEN").length;
  return (
    <header className="flex h-12 shrink-0 items-center gap-3 border-b px-3 md:px-4">
      <Button variant="ghost" size="icon-sm" className="md:hidden" onClick={() => setNavOpen(true)} aria-label="Open navigation">
        <Menu />
      </Button>
      <span className="text-sm font-semibold md:hidden">Atlas</span>
      <button
        onClick={() => setPalette(true)}
        className="hidden h-8 w-full max-w-sm items-center gap-2 rounded-md border bg-input/30 px-2.5 text-left text-sm text-muted-foreground hover:bg-input/50 md:flex"
      >
        <Search className="size-3.5" />
        Search places, links, monitors…
        <kbd className="ml-auto rounded border px-1.5 font-mono text-[10px]">{navigator.platform.startsWith("Mac") ? "⌘" : "Ctrl"} K</kbd>
      </button>
      <div className="ml-auto flex items-center gap-4">
        <UtcClock />
        <StreamPill />
        {internal && <Link
          to="/incidents"
          className={cn(
            "flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs lg:hidden",
            unacked > 0 ? "border-status-down/40 text-status-down" : "text-muted-foreground",
          )}
        >
          <Siren className="size-3.5" />
          <span className="tabular">{active.length}</span> active
        </Link>}
      </div>
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="w-64 bg-sidebar p-0">
          <SheetHeader className="p-0">
            <SheetTitle asChild><div><Logo expanded /></div></SheetTitle>
          </SheetHeader>
          <NavList expanded onNavigate={() => setNavOpen(false)} />
          <div className="mt-auto flex flex-col gap-1 border-t p-2">
            <SoundToggle expanded />
            <UserMenu expanded />
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}
